import { createAction, props } from '@ngrx/store';
import {
  Category,
  DepartmentListItemResponse,
  NotificationsListResponse,
  PaginatedApiResponse,
  PspCustomerSurchargeResponse,
  QualificationPricingListItemResponse,
  QualificationsListItemResponse,
  RequirementCategoryResponse,
  ShiftTimesResponse,
  UserItemResponse,
  UserProfileResponse,
  UserProfileSettingsUpdateRequest,
  UserProfileUpdateRequest,
  CatalogIndustrySurchargeResponse,
  CustomerDepartmentQualificationsListItemResponse,
  BranchListItemResponse,
} from '../../../api';
import { BaseApplicationTagType } from '../../shared/enums/base-application.enum';
import { BaseToastAlertInfoInterface } from '../../shared/interfaces/base-toast-alert-info.interface';
import { BaseCountry } from '../../shared/services/base-country.service';
import { UserItemFullNameType } from '../../modules/candidates/_types/base-user-full-name.type';

// Country List
export const loadCountryList = createAction('[Country List] Load list');
export const setCountryList = createAction('[Country List] Set list', props<{ payload: BaseCountry[] }>());
export const successCountryList = createAction('[Country List] Success');
export const errorCountryList = createAction('[Country List] Error');

// Qualifications
export const getQualifications = createAction(
  '[Candidates] Get qualifications',
  props<{ application: BaseApplicationTagType }>()
);
export const setQualifications = createAction(
  '[Candidates] Load qualifications',
  props<{ payload: QualificationsListItemResponse[] }>()
);
export const successLoadQualifications = createAction('[Candidates] Success load qualifications');
export const errorLoadQualifications = createAction('[Candidates] Error load qualifications');

// Qualification categories
export const getQualificationCategories = createAction(
  '[Candidates] Get qualification categories',
  props<{ application?: BaseApplicationTagType }>()
);
export const loadQualificationCategories = createAction(
  '[Candidates] Load qualification categories',
  props<{ payload: Array<Category> }>()
);
export const successLoadQualificationCategories = createAction('[Candidates] Success load qualification categories');
export const errorLoadQualificationCategories = createAction('[Candidates] Error load qualification categories');

// PSP Branches
export const loadPspBranches = createAction('[Shared] Load PSP branches', props<{ payload: { isActive?: boolean } }>());
export const setPspBranches = createAction('[Shared] Get PSP branches', props<{ payload: BranchListItemResponse[] }>());
export const successLoadPspBranches = createAction('[Shared] Success load PSP branches');
export const errorLoadPspBranches = createAction('[Shared] Error load PSP branches');
export const resetPspBranches = createAction('[Shared] Reset PSP branches');

// Logged in user
export const loadProfileData = createAction('[Administration: Profile] Load profile data');
export const setProfileData = createAction(
  '[Administration: User data] Store profile data',
  props<{ payload: UserProfileResponse | undefined }>()
);
export const successProfileData = createAction('[Administration: Profile] Profile fetched');
export const errorProfileData = createAction('[Administration: Profile] Profile fetch error');
export const updateProfileData = createAction(
  '[Administration: Profile] Update user profile',
  props<{ payload: UserProfileUpdateRequest }>()
);
export const updateProfileSettings = createAction(
  '[Administration: Profile] Update user profile settings',
  props<{ payload: UserProfileSettingsUpdateRequest }>()
);

// Requirements
export const getRequirements = createAction(
  '[Shared] Get requirements',
  props<{ application?: BaseApplicationTagType }>()
);
export const loadRequirements = createAction(
  '[Shared] Load requirements',
  props<{ payload: Array<RequirementCategoryResponse> }>()
);
export const successLoadRequirements = createAction('[Shared] Success load requirements');
export const errorLoadRequirements = createAction('[Shared] Error load requirements');
export const resetRequirements = createAction('[Shared] Reset requirements');

// Departments
export const loadCustomerDepartmentList = createAction(
  '[Shared] Load customer departments list',
  props<{ payload: { active?: boolean } }>()
);
export const setCustomerDepartmentList = createAction(
  '[Shared] Set customer departments list',
  props<{ payload: DepartmentListItemResponse[] }>()
);
export const successCustomerDepartmentList = createAction('[Shared] Success customer departments list');
export const errorCustomerDepartmentList = createAction('[Shared] Error customer departments list');
export const resetCustomerDepartmentList = createAction('[Shared] Reset customer departments list');

// Department users
export const getCustomerDepartmentUsers = createAction('[Shared] Get departmment users', props<{ payload: number }>());
export const loadCustomerDepartmentUsers = createAction(
  '[Shared] Load department users',
  props<{ payload: UserItemResponse[] }>()
);
export const successCustomerDepartmentUsers = createAction('[Shared] Success load department users');
export const errorCustomerDepartmentUsers = createAction('[Shared] Error load department users');
export const clearCustomerDepartmentUsers = createAction('[Shared] Clear department users');

// Departments qualifications list
export const loadCustomerDepartmentQualificationsList = createAction(
  '[Shared] Load customer departments qualifications list',
  props<{
    payload: {
      department: number;
      category?: number;
      tag?: BaseApplicationTagType;
    };
  }>()
);
export const setCustomerDepartmentQualificationsList = createAction(
  '[Shared] Set customer departments qualifications list',
  props<{ payload: CustomerDepartmentQualificationsListItemResponse[] }>()
);
export const resetCustomerDepartmentQualificationsList = createAction(
  '[Shared] Reset customer departments qualifications list'
);
export const successCustomerDepartmentQualificationsList = createAction(
  '[Shared] Success customer department qualifications list'
);
export const errorCustomerDepartmentQualificationsList = createAction(
  '[Shared] Error customer department qualifications list'
);

// PSP Price List
export const loadPspPriceList = createAction(
  '[Shared] Load PSP Price List',
  props<{ payload: { tag?: BaseApplicationTagType } }>()
);
export const setPspPriceList = createAction(
  '[Shared] Set PSP Price List',
  props<{ payload: QualificationPricingListItemResponse[] | undefined }>()
);
export const successPspPriceList = createAction('[Shared] Success PSP Price List');
export const errorPspPriceList = createAction('[Shared] Error PSP Price List');

// Fetch default times for shifts
export const fetchDefaultShiftTimes = createAction('[Inquiry] Fetch default shifts times');
export const loadDefaultShiftTimes = createAction(
  '[Inquiry] Load default shifts times',
  props<{ payload: ShiftTimesResponse }>()
);
export const loadDefaultShiftTimesSuccess = createAction('[Inquiry] Load default shift times success');
export const loadDefaultShiftTimesError = createAction('[Inquiry] Load default shift times error');

// PSP Customer Surcharges
export const loadPSPCustomerSurcharges = createAction(
  '[Shared] Load surcharges',
  props<{ payload: { customerId: number; locationId: number } }>()
);
export const setPSPCustomerSurcharges = createAction(
  '[Shared] Set surcharges',
  props<{ payload: PspCustomerSurchargeResponse }>()
);
export const successPSPCustomerSurcharges = createAction('[Shared] Success surcharges');
export const errorPSPCustomerSurcharges = createAction('[Shared] Error surcharges');
export const resetPSPCustomerSurcharges = createAction('[Shared] Reset surcharges');

// User Notifications
export const loadUserNotifications = createAction(
  '[Shared] Load User Notifications',
  props<{ payload: { page?: any; size?: any; isRead?: boolean } }>()
);
export const setUserNotifications = createAction(
  '[Shared] Set User Notifications',
  props<{ payload: PaginatedApiResponse & { data?: NotificationsListResponse[] } }>()
);

export const setNotificationReadState = createAction(
  '[Shared] Set Notification Read State',
  props<{ payload: { notification: number }; page?: any; size?: number }>()
);

export const setAllUserNotificationsAsRead = createAction('[Shared] Set All User Notifications as Read');

export const successUserNotifications = createAction('[Shared] Success User Notifications');
export const errorUserNotifications = createAction('[Shared] Error User Notifications');

// App Router
export const setCurrentAndPreviousRouteUrls = createAction(
  '[Shared] Set current and previous router urls',
  props<{ payload: { previousUrl: string; currentUrl: string } }>()
);

// Customer Department Favourite Qualifications List
export const loadCustomerDepartmentFavouriteQualificationList = createAction(
  '[Shared] Load Customer Department Favourite Qualifications List',
  props<{ payload: { department: number; location: number } }>()
);
export const setCustomerDepartmentFavouriteQualificationList = createAction(
  '[Shared] Set Customer Department Favourite Qualifications List',
  props<{ payload: QualificationsListItemResponse[] }>()
);
export const successCustomerDepartmentFavouriteQualificationList = createAction(
  '[Shared] Success Customer Department Favourite Qualifications List'
);
export const errorCustomerDepartmentFavouriteQualificationList = createAction(
  '[Shared] Error Customer Department Favourite Qualifications List'
);

// Language
export const setLanguage = createAction('[Shared] Set Language', props<{ language: string }>());
export const setLanguageSuccess = createAction('[Shared] Set Language Success', props<{ language: string }>());

// System Alerts
export const addSystemAlert = createAction(
  '[Shared] Add System Alert',
  props<{ payload: BaseToastAlertInfoInterface }>()
);
export const addUniqueSystemAlert = createAction(
  '[Shared] Add Unique System Alert',
  props<{ payload: BaseToastAlertInfoInterface }>()
);
export const deleteSystemAlert = createAction('[Shared] Delete System Alert', props<{ payload: number }>());

// Branch users
export const loadBranchUsers = createAction('[Shared] Load branch users', props<{ payload: { branch: number } }>());
export const setBranchUsers = createAction(
  '[Shared] Set branch users',
  props<{
    payload: { data: Array<UserItemFullNameType> | undefined; message: string | undefined };
  }>()
);
export const resetBranchUsers = createAction('[Shared] Reset branch users');
export const successBranchUsers = createAction('[Shared] Success branch users');
export const errorBranchUsers = createAction('[Shared] Error branch users');

export const setSidebarInitialOpening = createAction(
  '[Shared] Set inquiry sidebar initial opening',
  props<{ payload: boolean }>()
);

// Industry Surcharges
export const loadIndustrySurcharges = createAction('[Shared] Load Industry Surcharges');
export const setIndustrySurcharges = createAction(
  '[Shared] Set Industry Surcharges',
  props<{ payload: CatalogIndustrySurchargeResponse[] }>()
);
export const successIndustrySurcharges = createAction('[Shared] Success Industry Surcharges');
export const errorIndustrySurcharges = createAction('[Shared] Error Industry Surcharges');

// Calculated EP
export const loadCalculatedEPDate = createAction(
  '[Shared] Load Calculated EP',
  props<{ payload: { location: number; profile: number; possibleAssignmentStartDate: string } }>()
);
export const setCalculatedEPDate = createAction('[Shared] Set Calculated EP', props<{ payload: string }>());
export const successCalculatedEPDate = createAction('[Shared] Success Calculated EP');
export const errorCalculatedEPDate = createAction('[Shared] Error Calculated EP');
export const resetCalculatedEPDate = createAction('[Shared] Reset Calculated EP');

// Calculated MLP
export const loadCalculatedMLPDate = createAction(
  '[Shared] Load Calculated MLP',
  props<{ payload: { location: number; profile: number; possibleAssignmentStartDate: string } }>()
);
export const setCalculatedMLPDate = createAction('[Shared] Set Calculated MLP', props<{ payload: string }>());
export const successCalculatedMLPDate = createAction('[Shared] Success Calculated MLP');
export const errorCalculatedMLPDate = createAction('[Shared] Error Calculated MLP');
export const resetCalculatedMLPDate = createAction('[Shared] Reset Calculated MLP');
